import { authenticatedFetch } from "../components/Global/authService";
import { FileMetadata } from "../Models/FileMetadata";

export const rolesList = [
  "ALL",
  "ADMIN",
  "COUNSELLOR",
  "CERTIFICATE",
  "CENTER HEAD",
  "FACULTY",
  "STUDENT"
]

const SCREENS_CACHE_KEY = 'nict-allowed-screens-v3';

let allowedScreensRequest: Promise<string[]> | null = null;

export interface RoleDefinition {
  role: string;
  displayName?: string;
  active?: boolean;
  systemRole?: boolean;
}

export const fetchDynamicRoles = async (): Promise<string[]> => {
  try {
    const response = await authenticatedFetch('/authz/roles?activeOnly=true');
    if (!response.ok) {
      return rolesList;
    }
    const payload: RoleDefinition[] = await response.json();
    const fetched = (payload || [])
      .map((row) => String(row.role || '').trim().toUpperCase())
      .filter(Boolean);

    return fetched.length ? Array.from(new Set(fetched)) : rolesList;
  } catch (error) {
    console.warn('Falling back to static roles list:', error);
    return rolesList;
  }
};

const normalizeRole = (role?: string) => String(role || '').trim().toUpperCase();

const topNavOrderByRole: Record<string, string[]> = {
  ADMIN: ['/', '/sales-performance', '/contracts', '/attendance/admin', '/enquiry', '/fee-collection', '/seminars', '/myschedule', '/certificate', '/course', '/my-courses', '/svcs'],
  'CENTER HEAD': ['/', '/sales-performance', '/contracts', '/attendance/admin', '/enquiry', '/fee-collection', '/seminars', '/myschedule', '/svcs', '/my-courses'],
  COUNSELLOR: ['/', '/sales-performance', '/attendance/admin', '/enquiry', '/fee-collection', '/seminars', '/myschedule', '/svcs'],
  FACULTY: ['/', '/attendance/trainee', '/myschedule', '/my-courses'],
  STUDENT: ['/', '/my-courses'],
  MAINTAINER: ['/', '/certificate', '/course', '/my-courses', '/svcs'],
  CERTIFICATE: ['/', '/certificate'],
};

const servicesOrderByRole: Record<string, string[]> = {
  ADMIN: ['/user-mgmt', '/notice', '/timetable', '/offer', '/editwebsite', '/ebooks', '/audit-log', '/fee-collection', '/fee-overdue'],
  'CENTER HEAD': ['/user-mgmt', '/notice', '/timetable', '/fee-collection', '/fee-overdue'],
  COUNSELLOR: ['/timetable', '/fee-collection', '/fee-overdue'],
  MAINTAINER: ['/notice', '/ebooks'],
};

const seminarsOrderByRole: Record<string, string[]> = {
  ADMIN: ['/seminars', '/seminars/add'],
  COUNSELLOR: ['/seminars', '/seminars/add'],
  'CENTER HEAD': ['/seminars'],
};

const hiddenRouteOrderByRole: Record<string, string[]> = {
  ADMIN: ['/profile', '/seminars/edit/:id', '/seminars/:id'],
  COUNSELLOR: ['/profile', '/seminars/edit/:id', '/seminars/:id'],
  'CENTER HEAD': ['/profile', '/seminars/:id'],
  FACULTY: ['/profile'],
  STUDENT: ['/profile'],
  MAINTAINER: ['/profile'],
  CERTIFICATE: ['/profile'],
};

const fallbackAllowedScreensByRole = (role?: string): string[] => {
  const normalizedRole = normalizeRole(role);
  const top = topNavOrderByRole[normalizedRole] || ['/'];
  const services = servicesOrderByRole[normalizedRole] || [];
  const seminars = seminarsOrderByRole[normalizedRole] || [];
  const hidden = hiddenRouteOrderByRole[normalizedRole] || ['/profile'];
  return Array.from(new Set([...top, ...services, ...seminars, ...hidden]));
};

export const getCachedAllowedScreens = (): string[] => {
  const raw = localStorage.getItem(SCREENS_CACHE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((item) => String(item || '').trim()).filter(Boolean);
  } catch {
    return [];
  }
};

export const clearAllowedScreensCache = () => {
  localStorage.removeItem(SCREENS_CACHE_KEY);
  allowedScreensRequest = null;
};

export const fetchMyAllowedScreens = async (roleFallback?: string): Promise<string[]> => {
  const cached = getCachedAllowedScreens();
  if (cached.length) {
    return cached;
  }

  if (!allowedScreensRequest) {
    allowedScreensRequest = (async () => {
      try {
        const response = await authenticatedFetch('/authz/me/screens');
        if (!response.ok) {
          return fallbackAllowedScreensByRole(roleFallback);
        }
        const payload = await response.json();
        const screens = (payload?.screens || [])
          .map((value: unknown) => String(value || '').trim())
          .filter(Boolean);
        if (screens.length) {
          localStorage.setItem(SCREENS_CACHE_KEY, JSON.stringify(Array.from(new Set(screens))));
          return Array.from(new Set(screens));
        }
        return fallbackAllowedScreensByRole(roleFallback);
      } catch (error) {
        console.warn('Falling back to role-based screen list:', error);
        return fallbackAllowedScreensByRole(roleFallback);
      }
    })();
  }

  return allowedScreensRequest;
};

export const isScreenAllowed = (screenKey: string, allowedScreens: string[]): boolean => {
  const normalized = String(screenKey || '').trim();
  if (!normalized) {
    return false;
  }
  const screenSet = new Set((allowedScreens || []).map((screen) => String(screen || '').trim()));
  if (normalized === '/fee-collection' && screenSet.has('/enquiry')) {
    return true;
  }
  return screenSet.has(normalized);
};

export const getPresignedUrl = async (preSignedUrl:string) => {
    try {
      // Request a pre-signed URL from the backend
      const response = await authenticatedFetch(preSignedUrl);
      const { url } = await response.json();
      return url

    } catch (error) {
      console.error('Unable to get Presigned Url:', error);
    }
  };

export const uploadToS3 = async (s3url:string,selectedFile:FileMetadata) => {
          await fetch(s3url, {
            method: 'PUT',
            headers: {
              'Content-Type': selectedFile.filetype,
            },
            body: selectedFile.file,
          });
    
}


export const fetchS3Objects = async (folder: string) => {
  try {
    const response = await authenticatedFetch(`/s3/listobjects?folder=${encodeURI(folder)}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: string[] = await response.json();
    return data
  } catch (err: any) {
    console.log(err.message);
  } 
};

export const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};


export const formatDate = (date:string): string => {
  const today = new Date(date);
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
};

export const displayFormattedDate = (date:string): string => {
  const today = new Date(date);
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


 export const timeToMinutes = (timeStr: string) =>{
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
  
    if (modifier === 'PM' && hours !== 12) hours += 12;
    if (modifier === 'AM' && hours === 12) hours = 0;
  
    return hours * 60 + minutes;
  }